import { Link } from 'react-router-dom';
import Navbar from './home/components/Navbar';
import Footer from './home/components/Footer';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageSeo } from '../hooks/usePageSeo';
import { LinkedText } from '../components/LinkedText';

type GuideBlock = {
  en_heading: string;
  bm_heading: string;
  en_paragraphs: string[];
  bm_paragraphs: string[];
  en_points?: string[];
  bm_points?: string[];
};

type GuideSection = {
  id: string;
  en_heading: string;
  bm_heading: string;
  en_intro: string[];
  bm_intro: string[];
  blocks: GuideBlock[];
};

const guideSections: GuideSection[] = [
  {
    id: 'what-is-responsible-gaming',
    en_heading: 'What Is Responsible Gaming?',
    bm_heading: 'Apakah Permainan Bertanggungjawab?',
    en_intro: [
      'Responsible gaming means making informed decisions and maintaining control over how much time and money you spend on gambling. Online casino games should remain occasional entertainment within a budget decided before play begins.',
      'The amount spent should be money that can be comfortably lost without affecting essential expenses, savings, family commitments or other financial responsibilities.',
    ],
    bm_intro: [
      'Permainan bertanggungjawab bermaksud membuat keputusan bermaklumat dan mengekalkan kawalan terhadap masa serta wang yang digunakan untuk berjudi. Permainan kasino dalam talian perlu kekal sebagai hiburan sekali-sekala dalam bajet yang ditetapkan sebelum bermain.',
      'Jumlah yang dibelanjakan mestilah wang yang mampu dirugikan tanpa menjejaskan perbelanjaan penting, simpanan, komitmen keluarga atau tanggungjawab kewangan lain.',
    ],
    blocks: [
      {
        en_heading: 'Gambling is entertainment, not income',
        bm_heading: 'Perjudian ialah hiburan, bukan pendapatan',
        en_paragraphs: [
          'Slots, live casino games and other real-money products involve financial risk. Players may sometimes win, but losses are also a normal and unavoidable possibility.',
          'Before playing, ask: “Am I comfortable losing the full amount I plan to spend today?” If the answer is no, reduce the budget or choose not to play.',
        ],
        bm_paragraphs: [
          'Slot, kasino langsung dan produk wang sebenar lain melibatkan risiko kewangan. Pemain mungkin menang, tetapi kerugian juga merupakan kemungkinan biasa yang tidak dapat dielakkan.',
          'Sebelum bermain, tanya: “Adakah saya selesa kehilangan keseluruhan jumlah yang dirancang hari ini?” Jika tidak, kurangkan bajet atau jangan bermain.',
        ],
      },
      {
        en_heading: 'Chance cannot be predicted',
        bm_heading: 'Peluang tidak boleh diramal',
        en_paragraphs: [
          'A losing streak does not mean a win is due. Previous results cannot reliably predict the next spin, hand or game result, and no casino outcome should be treated as guaranteed.',
        ],
        bm_paragraphs: [
          'Siri kerugian tidak bermakna kemenangan akan tiba. Keputusan lalu tidak boleh meramal putaran, tangan atau keputusan seterusnya, dan tiada hasil kasino boleh dianggap terjamin.',
        ],
      },
    ],
  },
  {
    id: 'set-a-budget',
    en_heading: 'Set a Gambling Budget Before You Play',
    bm_heading: 'Tetapkan Bajet Perjudian Sebelum Bermain',
    en_intro: [
      'One of the simplest safer-gaming habits is deciding how much you can afford to spend before starting. Keep the gambling budget separate from essential money and stop when the limit is reached.',
    ],
    bm_intro: [
      'Salah satu tabiat permainan lebih selamat ialah menentukan jumlah yang mampu dibelanjakan sebelum bermula. Asingkan bajet perjudian daripada wang keperluan dan berhenti apabila had dicapai.',
    ],
    blocks: [
      {
        en_heading: 'Protect essential household money',
        bm_heading: 'Lindungi wang keperluan rumah',
        en_paragraphs: [
          'Gambling should never compete with essential financial commitments. Decide the entertainment amount only after necessities and savings have been considered.',
        ],
        bm_paragraphs: [
          'Perjudian tidak sepatutnya bersaing dengan komitmen kewangan penting. Tentukan jumlah hiburan hanya selepas keperluan dan simpanan diambil kira.',
        ],
        en_points: [
          'Rent or housing',
          'Food and groceries',
          'Bills and utilities',
          'Loan repayments',
          'Medical and children’s expenses',
          'Transportation, savings and emergencies',
        ],
        bm_points: [
          'Sewa atau perumahan',
          'Makanan dan barangan dapur',
          'Bil dan utiliti',
          'Bayaran balik pinjaman',
          'Perubatan dan perbelanjaan anak',
          'Pengangkutan, simpanan dan kecemasan',
        ],
      },
      {
        en_heading: 'Never borrow money to gamble',
        bm_heading: 'Jangan pinjam wang untuk berjudi',
        en_paragraphs: [
          'Avoid loans, borrowed money, credit intended for essentials or money belonging to another person. Borrowing turns entertainment into a financial obligation and makes losses harder to manage.',
          'Borrowing specifically to continue gambling is an important warning sign that it is time to stop and seek support.',
        ],
        bm_paragraphs: [
          'Elakkan pinjaman, wang yang dipinjam, kredit untuk keperluan atau wang milik orang lain. Meminjam menjadikan hiburan sebagai tanggungan kewangan dan menyukarkan pengurusan kerugian.',
          'Meminjam khusus untuk terus berjudi ialah tanda amaran penting bahawa sudah tiba masanya untuk berhenti dan mendapatkan sokongan.',
        ],
      },
      {
        en_heading: 'Do not increase the budget after losing',
        bm_heading: 'Jangan tambah bajet selepas kalah',
        en_paragraphs: [
          'Depositing more to recover money already lost is called chasing losses. The next spin, hand or bet is not guaranteed to recover anything and may create a larger loss.',
          'Treat money already spent as an entertainment cost rather than an amount that must be won back.',
        ],
        bm_paragraphs: [
          'Deposit lebih banyak untuk mendapatkan semula wang yang hilang dikenali sebagai mengejar kerugian. Putaran, tangan atau pertaruhan seterusnya tidak menjamin pemulihan dan boleh menambah kerugian.',
          'Anggap wang yang telah dibelanjakan sebagai kos hiburan, bukan jumlah yang mesti dimenangi semula.',
        ],
      },
    ],
  },
  {
    id: 'time-and-emotions',
    en_heading: 'Set Time Limits for Online Casino Gaming',
    bm_heading: 'Tetapkan Had Masa untuk Permainan Kasino Dalam Talian',
    en_intro: [
      'Responsible gaming is not only about money. Digital casino games can make it easy to continue longer than planned, so decide a stopping time before the session begins.',
    ],
    bm_intro: [
      'Permainan bertanggungjawab bukan hanya tentang wang. Permainan digital memudahkan sesi berlanjutan lebih lama daripada dirancang, maka tentukan masa berhenti sebelum sesi bermula.',
    ],
    blocks: [
      {
        en_heading: 'Take regular breaks',
        bm_heading: 'Berehat secara berkala',
        en_paragraphs: [
          'Long sessions can make it harder to track decisions, spending and emotions. Step away from the screen, move around, drink water or do something unrelated to gambling.',
          'A break creates space to decide whether you genuinely want to continue or are simply reacting to the previous result.',
        ],
        bm_paragraphs: [
          'Sesi panjang menyukarkan pemantauan keputusan, perbelanjaan dan emosi. Tinggalkan skrin seketika, bergerak, minum air atau lakukan aktiviti lain.',
          'Rehat memberi ruang untuk menentukan sama ada anda benar-benar mahu meneruskan atau hanya bertindak balas terhadap keputusan sebelumnya.',
        ],
      },
      {
        en_heading: 'Avoid gambling when tired or emotional',
        bm_heading: 'Elakkan bermain ketika letih atau beremosi',
        en_paragraphs: [
          'Decision-making can be affected by fatigue, anger, stress, personal problems, financial pressure, alcohol or other substances. Casino decisions should be made with a clear mind rather than as an emotional reaction.',
        ],
        bm_paragraphs: [
          'Keputusan boleh dipengaruhi oleh keletihan, kemarahan, tekanan, masalah peribadi, tekanan kewangan, alkohol atau bahan lain. Keputusan kasino perlu dibuat dengan fikiran jelas, bukan sebagai tindak balas emosi.',
        ],
        en_points: ['Extremely tired', 'Angry or upset', 'Under financial pressure', 'Trying to escape a problem', 'Under the influence of alcohol or substances'],
        bm_points: ['Sangat letih', 'Marah atau kecewa', 'Di bawah tekanan kewangan', 'Cuba melarikan diri daripada masalah', 'Di bawah pengaruh alkohol atau bahan lain'],
      },
    ],
  },
  {
    id: 'understand-games',
    en_heading: 'Understand How Casino Games Work',
    bm_heading: 'Fahami Cara Permainan Kasino Berfungsi',
    en_intro: [
      'Casino games involve chance and outcomes cannot be guaranteed. Understanding this basic fact helps players avoid decisions based on common gambling misconceptions.',
    ],
    bm_intro: [
      'Permainan kasino melibatkan peluang dan keputusan tidak boleh dijamin. Memahami fakta asas ini membantu pemain mengelakkan keputusan berdasarkan salah faham perjudian.',
    ],
    blocks: [
      {
        en_heading: 'Previous results do not guarantee future results',
        bm_heading: 'Keputusan lalu tidak menjamin keputusan akan datang',
        en_paragraphs: [
          'Repeatedly losing on a slot does not mean the machine must provide a win soon. Previous losses should never be used as a reason to increase the wager.',
          'Read the rules, paytable and game information, but remember that understanding mechanics does not remove the possibility of losing.',
        ],
        bm_paragraphs: [
          'Kalah berulang kali pada slot tidak bermakna mesin mesti memberikan kemenangan tidak lama lagi. Kerugian lalu tidak boleh dijadikan alasan untuk meningkatkan pertaruhan.',
          'Baca peraturan, paytable dan maklumat permainan, tetapi memahami mekanik tidak menghapuskan kemungkinan kalah.',
        ],
      },
      {
        en_heading: 'A strategy cannot guarantee a win',
        bm_heading: 'Strategi tidak boleh menjamin kemenangan',
        en_paragraphs: [
          'Some games involve player decisions, but no betting system can eliminate gambling risk. Be cautious of anyone selling a secret strategy, prediction tool or guaranteed-profit method.',
          'Claims of a “guaranteed win” are misleading because real-money casino outcomes always carry risk.',
        ],
        bm_paragraphs: [
          'Sesetengah permainan melibatkan keputusan pemain, tetapi tiada sistem pertaruhan boleh menghapuskan risiko. Berhati-hati dengan strategi rahsia, alat ramalan atau kaedah keuntungan terjamin.',
          'Dakwaan “kemenangan terjamin” mengelirukan kerana keputusan kasino wang sebenar sentiasa berisiko.',
        ],
      },
    ],
  },
  {
    id: 'warning-signs',
    en_heading: 'Recognising Signs of Problem Gambling',
    bm_heading: 'Mengenali Tanda Perjudian Bermasalah',
    en_intro: [
      'Gambling habits can change over time. Something that starts as occasional entertainment can become difficult to control, so noticing behavioural changes early is important.',
      'One warning sign does not define a person, but it should not be ignored. Several signs together indicate that a break and additional support may be appropriate.',
    ],
    bm_intro: [
      'Tabiat perjudian boleh berubah dari masa ke masa. Aktiviti yang bermula sebagai hiburan sekali-sekala boleh menjadi sukar dikawal, maka perubahan tingkah laku perlu disedari awal.',
      'Satu tanda amaran tidak menentukan seseorang, tetapi tidak patut diabaikan. Beberapa tanda serentak menunjukkan rehat dan sokongan tambahan mungkin diperlukan.',
    ],
    blocks: [
      {
        en_heading: 'Warning signs to look out for',
        bm_heading: 'Tanda amaran yang perlu diperhatikan',
        en_paragraphs: [
          'Review your habits honestly and compare current spending, frequency and emotions with how you played several months ago.',
        ],
        bm_paragraphs: [
          'Nilai tabiat anda dengan jujur dan bandingkan perbelanjaan, kekerapan serta emosi semasa dengan beberapa bulan lalu.',
        ],
        en_points: [
          'Spending more money or time than planned',
          'Frequently chasing losses or increasing bets',
          'Borrowing money or using essential funds',
          'Hiding gambling from family or friends',
          'Feeling anxious or irritated when unable to play',
          'Neglecting work, family or responsibilities',
          'Thinking about gambling for much of the day',
          'Feeling unable to stop despite wanting to',
        ],
        bm_points: [
          'Berbelanja lebih banyak wang atau masa daripada dirancang',
          'Kerap mengejar kerugian atau meningkatkan pertaruhan',
          'Meminjam wang atau menggunakan dana keperluan',
          'Menyembunyikan perjudian daripada keluarga atau rakan',
          'Cemas atau marah apabila tidak dapat bermain',
          'Mengabaikan kerja, keluarga atau tanggungjawab',
          'Memikirkan perjudian sepanjang hari',
          'Berasa tidak mampu berhenti walaupun mahu',
        ],
      },
    ],
  },
  {
    id: 'self-assessment',
    en_heading: 'Responsible Gaming Self-Assessment',
    bm_heading: 'Penilaian Kendiri Permainan Bertanggungjawab',
    en_intro: [
      'If you are unsure whether gambling remains under control, pause and answer the following questions honestly. This is a personal reflection tool, not a medical diagnosis.',
    ],
    bm_intro: [
      'Jika anda tidak pasti sama ada perjudian masih terkawal, berhenti seketika dan jawab soalan berikut dengan jujur. Ini ialah alat refleksi peribadi, bukan diagnosis perubatan.',
    ],
    blocks: [
      {
        en_heading: 'Ask yourself these questions',
        bm_heading: 'Tanya diri anda soalan ini',
        en_paragraphs: [
          'If you answer yes to several questions, consider taking an extended break, requesting account limits and speaking with someone you trust or an appropriate professional support service.',
        ],
        bm_paragraphs: [
          'Jika anda menjawab ya kepada beberapa soalan, pertimbangkan rehat lebih panjang, minta had akaun dan berbincang dengan orang dipercayai atau perkhidmatan sokongan profesional.',
        ],
        en_points: [
          'Do I regularly spend more than planned?',
          'Do I gamble to recover previous losses?',
          'Have I borrowed money specifically to gamble?',
          'Have I hidden my gambling from people close to me?',
          'Has gambling affected work, relationships or responsibilities?',
          'Do I feel stressed, angry or anxious because of gambling?',
          'Do I find it difficult to stop once I begin?',
          'Have I used money intended for bills or essentials?',
        ],
        bm_points: [
          'Adakah saya kerap berbelanja lebih daripada dirancang?',
          'Adakah saya berjudi untuk mendapatkan semula kerugian?',
          'Pernahkah saya meminjam wang khusus untuk berjudi?',
          'Pernahkah saya menyembunyikan perjudian daripada orang rapat?',
          'Adakah perjudian menjejaskan kerja, hubungan atau tanggungjawab?',
          'Adakah perjudian menyebabkan tekanan, marah atau cemas?',
          'Adakah saya sukar berhenti selepas bermula?',
          'Pernahkah saya menggunakan wang bil atau keperluan?',
        ],
      },
    ],
  },
  {
    id: 'stop-and-get-help',
    en_heading: 'What to Do When Gambling Stops Being Fun',
    bm_heading: 'Apa yang Perlu Dilakukan Apabila Perjudian Tidak Lagi Menyeronokkan',
    en_intro: [
      'There is nothing wrong with deciding to stop. If casino gaming creates stress, financial pressure, conflict or other negative consequences, continuing to play is unlikely to solve the situation.',
    ],
    bm_intro: [
      'Tidak salah untuk memutuskan berhenti. Jika permainan kasino menyebabkan tekanan, masalah kewangan, konflik atau akibat negatif lain, terus bermain tidak mungkin menyelesaikan keadaan.',
    ],
    blocks: [
      {
        en_heading: 'Take a break from gambling',
        bm_heading: 'Berehat daripada perjudian',
        en_paragraphs: [
          'A temporary break creates distance from the urge to continue. Avoid new deposits and replace gambling time with exercise, hobbies, social activities or time with family.',
          'If a short break is not enough, contact support to ask about stronger account restrictions or self-exclusion.',
        ],
        bm_paragraphs: [
          'Rehat sementara mewujudkan jarak daripada dorongan untuk terus bermain. Elakkan deposit baharu dan gantikan masa perjudian dengan senaman, hobi, aktiviti sosial atau keluarga.',
          'Jika rehat singkat tidak mencukupi, hubungi sokongan untuk bertanya tentang sekatan akaun atau pengecualian diri.',
        ],
      },
      {
        en_heading: 'Talk to someone you trust',
        bm_heading: 'Bercakap dengan seseorang yang dipercayai',
        en_paragraphs: [
          'Gambling difficulties can become harder to manage when kept completely private. Speak with a trusted family member, friend, counsellor or appropriate professional support service.',
          'You do not need to wait until the problem becomes severe. Seeking help early can prevent financial and emotional harm from growing.',
        ],
        bm_paragraphs: [
          'Kesukaran perjudian menjadi lebih sukar apabila dirahsiakan sepenuhnya. Bercakap dengan ahli keluarga, rakan, kaunselor atau perkhidmatan sokongan profesional.',
          'Anda tidak perlu menunggu sehingga masalah menjadi teruk. Mendapatkan bantuan awal boleh menghalang kemudaratan kewangan dan emosi daripada bertambah.',
        ],
      },
    ],
  },
  {
    id: 'protect-minors-security',
    en_heading: 'Protecting Minors & Keeping Your Account Secure',
    bm_heading: 'Melindungi Bawah Umur & Menjaga Keselamatan Akaun',
    en_intro: [
      'MrCuci real-money gaming services are intended only for eligible adults aged 18 and above. Adults sharing devices or a household with minors should take extra precautions against unauthorised access.',
    ],
    bm_intro: [
      'Perkhidmatan permainan wang sebenar MrCuci hanya untuk orang dewasa yang layak berumur 18 tahun ke atas. Orang dewasa yang berkongsi peranti atau rumah dengan bawah umur perlu mengambil langkah tambahan.',
    ],
    blocks: [
      {
        en_heading: 'How to prevent underage access',
        bm_heading: 'Cara mencegah akses bawah umur',
        en_paragraphs: [
          'Do not allow any other person, including a minor, to use your casino account. Account owners are responsible for protecting login credentials and payment access.',
        ],
        bm_paragraphs: [
          'Jangan benarkan orang lain, termasuk bawah umur, menggunakan akaun kasino anda. Pemilik akaun bertanggungjawab melindungi log masuk dan akses pembayaran.',
        ],
        en_points: [
          'Never share casino login information',
          'Use strong passwords and device locks',
          'Log out after playing on a shared device',
          'Do not save passwords on shared browsers',
          'Keep payment information secure',
          'Use parental controls where appropriate',
        ],
        bm_points: [
          'Jangan kongsi maklumat log masuk kasino',
          'Gunakan kata laluan kuat dan kunci peranti',
          'Log keluar selepas bermain pada peranti dikongsi',
          'Jangan simpan kata laluan pada pelayar dikongsi',
          'Pastikan maklumat pembayaran selamat',
          'Gunakan kawalan ibu bapa jika sesuai',
        ],
      },
      {
        en_heading: 'Use strong account security',
        bm_heading: 'Gunakan keselamatan akaun yang kuat',
        en_paragraphs: [
          'Use a unique password rather than reusing one from another website. Never share passwords, one-time codes, banking information or other sensitive credentials.',
          'Access the account through the official MrCuci website. If you notice unfamiliar activity, change the password and contact official customer support promptly.',
        ],
        bm_paragraphs: [
          'Gunakan kata laluan unik dan jangan gunakan semula kata laluan laman lain. Jangan kongsi kata laluan, kod sekali guna, maklumat bank atau kelayakan sensitif.',
          'Akses akaun melalui laman rasmi MrCuci. Jika terdapat aktiviti tidak dikenali, tukar kata laluan dan hubungi sokongan rasmi segera.',
        ],
      },
    ],
  },
  {
    id: 'track-activity',
    en_heading: 'Keep Track of Your Gambling Activity',
    bm_heading: 'Pantau Aktiviti Perjudian Anda',
    en_intro: [
      'Individual deposits or sessions can look small in isolation. Reviewing the complete weekly or monthly picture makes it easier to understand actual spending and playing frequency.',
    ],
    bm_intro: [
      'Deposit atau sesi individu mungkin kelihatan kecil secara berasingan. Menyemak gambaran mingguan atau bulanan penuh memudahkan pemahaman perbelanjaan dan kekerapan sebenar.',
    ],
    blocks: [
      {
        en_heading: 'Review spending regularly',
        bm_heading: 'Semak perbelanjaan secara berkala',
        en_paragraphs: [
          'Record deposits, withdrawals and net spending over a fixed period. Compare the total with the entertainment budget established before play.',
          'A small amount repeated frequently can become substantial. Reduce the budget or stop if the overall total no longer fits comfortably within your finances.',
        ],
        bm_paragraphs: [
          'Catat deposit, pengeluaran dan perbelanjaan bersih dalam tempoh tetap. Bandingkan jumlah dengan bajet hiburan yang ditetapkan.',
          'Jumlah kecil yang berulang boleh menjadi besar. Kurangkan bajet atau berhenti jika jumlah keseluruhan tidak lagi sesuai dengan kewangan.',
        ],
      },
      {
        en_heading: 'Pay attention to playing frequency',
        bm_heading: 'Perhatikan kekerapan bermain',
        en_paragraphs: [
          'Compare how often you play now with several months ago. If occasional play has gradually become a daily activity, ask whether the change was intentional and whether it affects other priorities.',
        ],
        bm_paragraphs: [
          'Bandingkan kekerapan bermain sekarang dengan beberapa bulan lalu. Jika permainan sekali-sekala menjadi aktiviti harian, tanya sama ada perubahan itu disengajakan dan menjejaskan keutamaan lain.',
        ],
      },
    ],
  },
  {
    id: 'common-myths',
    en_heading: 'Common Responsible Gaming Myths',
    bm_heading: 'Mitos Umum Permainan Bertanggungjawab',
    en_intro: [
      'Recognising misleading beliefs helps players make decisions based on risk and facts rather than emotion.',
    ],
    bm_intro: [
      'Mengenali kepercayaan mengelirukan membantu pemain membuat keputusan berdasarkan risiko dan fakta, bukan emosi.',
    ],
    blocks: [
      {
        en_heading: '“I have lost several times, so I must win soon”',
        bm_heading: '“Saya sudah kalah beberapa kali, jadi saya mesti menang tidak lama lagi”',
        en_paragraphs: [
          'Previous losses do not guarantee a future win. Do not increase a wager because you believe a winning result is due.',
        ],
        bm_paragraphs: [
          'Kerugian lalu tidak menjamin kemenangan akan datang. Jangan tingkatkan pertaruhan kerana percaya kemenangan akan tiba.',
        ],
      },
      {
        en_heading: '“I can win back everything I lost”',
        bm_heading: '“Saya boleh memenangi semula semua yang hilang”',
        en_paragraphs: [
          'Another session may produce a win or another loss. Repeated attempts to recover losses can increase the total financial harm.',
        ],
        bm_paragraphs: [
          'Sesi lain boleh menghasilkan kemenangan atau kerugian. Percubaan berulang untuk memulihkan kerugian boleh meningkatkan kemudaratan kewangan.',
        ],
      },
      {
        en_heading: '“A bigger bet improves my chance of recovery”',
        bm_heading: '“Pertaruhan lebih besar meningkatkan peluang untuk pulih”',
        en_paragraphs: [
          'A larger wager increases the amount at risk; it does not remove the possibility of losing or guarantee recovery.',
        ],
        bm_paragraphs: [
          'Pertaruhan lebih besar meningkatkan jumlah berisiko; ia tidak menghapuskan kemungkinan kalah atau menjamin pemulihan.',
        ],
      },
      {
        en_heading: '“Gambling can solve my financial problems”',
        bm_heading: '“Perjudian boleh menyelesaikan masalah kewangan saya”',
        en_paragraphs: [
          'Gambling adds financial uncertainty and should never be used as a solution to debt, bills or income problems.',
        ],
        bm_paragraphs: [
          'Perjudian menambah ketidakpastian kewangan dan tidak boleh digunakan sebagai penyelesaian hutang, bil atau masalah pendapatan.',
        ],
      },
    ],
  },
  {
    id: 'safer-play-checklist',
    en_heading: 'Responsible Online Casino Checklist',
    bm_heading: 'Senarai Semak Kasino Dalam Talian Bertanggungjawab',
    en_intro: [
      'Responsible gaming is ultimately about maintaining control. Use this checklist before opening a real-money game.',
    ],
    bm_intro: [
      'Permainan bertanggungjawab ialah tentang mengekalkan kawalan. Gunakan senarai semak ini sebelum membuka permainan wang sebenar.',
    ],
    blocks: [
      {
        en_heading: 'Before you play',
        bm_heading: 'Sebelum anda bermain',
        en_paragraphs: [
          'If you no longer feel comfortable with the money or time being spent, stop and reassess. Limits work only when they are followed.',
        ],
        bm_paragraphs: [
          'Jika anda tidak lagi selesa dengan wang atau masa yang digunakan, berhenti dan nilai semula. Had hanya berfungsi apabila dipatuhi.',
        ],
        en_points: [
          'Decide the budget',
          'Decide the session length',
          'Understand the game and rules',
          'Accept that losing is possible',
          'Never chase previous losses',
          'Stop when either limit is reached',
        ],
        bm_points: [
          'Tentukan bajet',
          'Tentukan tempoh sesi',
          'Fahami permainan dan peraturan',
          'Terima bahawa kerugian boleh berlaku',
          'Jangan mengejar kerugian lalu',
          'Berhenti apabila mana-mana had dicapai',
        ],
      },
    ],
  },
];

const responsibleFaqs = [
  {
    en_q: 'What does responsible gaming mean?',
    bm_q: 'Apakah maksud permainan bertanggungjawab?',
    en_a: 'Responsible gaming means treating gambling as entertainment while maintaining control over time, spending and behaviour. It includes understanding risk, setting limits, avoiding loss chasing and recognising negative effects early.',
    bm_a: 'Permainan bertanggungjawab bermaksud menganggap perjudian sebagai hiburan sambil mengawal masa, perbelanjaan dan tingkah laku. Ia termasuk memahami risiko, menetapkan had, mengelakkan mengejar kerugian dan mengenali kesan negatif awal.',
  },
  {
    en_q: 'How much should I spend on online casino games?',
    bm_q: 'Berapa banyak patut saya belanjakan pada permainan kasino?',
    en_a: 'There is no single amount suitable for everyone. Use only discretionary entertainment money that can be lost without affecting essential expenses, commitments or savings.',
    bm_a: 'Tiada satu jumlah yang sesuai untuk semua orang. Gunakan hanya wang hiburan yang mampu dirugikan tanpa menjejaskan perbelanjaan penting, komitmen atau simpanan.',
  },
  {
    en_q: 'Should I continue playing to recover a casino loss?',
    bm_q: 'Patutkah saya terus bermain untuk mendapatkan semula kerugian?',
    en_a: 'No result is guaranteed. Continuing specifically to recover a loss is chasing losses and can increase overall financial risk.',
    bm_a: 'Tiada keputusan yang dijamin. Terus bermain khusus untuk memulihkan kerugian ialah mengejar kerugian dan boleh meningkatkan risiko kewangan.',
  },
  {
    en_q: 'Can online casino games guarantee winnings?',
    bm_q: 'Bolehkah permainan kasino dalam talian menjamin kemenangan?',
    en_a: 'No. Real-money casino games involve chance and financial risk. No legitimate strategy can guarantee continuous winnings.',
    bm_a: 'Tidak. Permainan kasino wang sebenar melibatkan peluang dan risiko kewangan. Tiada strategi sah boleh menjamin kemenangan berterusan.',
  },
  {
    en_q: 'When should I take a break from gambling?',
    bm_q: 'Bilakah saya perlu berehat daripada perjudian?',
    en_a: 'Take a break when you exceed a money or time limit, feel emotionally affected by losses, begin chasing losses or notice gambling interfering with daily life.',
    bm_a: 'Berehat apabila melebihi had wang atau masa, terjejas secara emosi oleh kerugian, mula mengejar kerugian atau perjudian mengganggu kehidupan harian.',
  },
  {
    en_q: 'Is MrCuci intended for children?',
    bm_q: 'Adakah MrCuci ditujukan kepada kanak-kanak?',
    en_a: 'No. Real-money services are intended only for eligible adults aged 18 and above. Parents and guardians should protect account credentials, payment information and devices from minors.',
    bm_a: 'Tidak. Perkhidmatan wang sebenar hanya untuk orang dewasa yang layak berumur 18 tahun ke atas. Ibu bapa dan penjaga perlu melindungi akaun, pembayaran dan peranti daripada bawah umur.',
  },
  {
    en_q: 'Where can I get additional responsible gambling help?',
    bm_q: 'Di mana saya boleh mendapatkan bantuan perjudian bertanggungjawab?',
    en_a: 'Contact MrCuci support to discuss account restrictions, and consider speaking with an appropriate professional support service or recognised responsible gambling organisation available in your location.',
    bm_a: 'Hubungi sokongan MrCuci untuk membincangkan sekatan akaun, dan pertimbangkan perkhidmatan sokongan profesional atau organisasi perjudian bertanggungjawab yang diiktiraf di lokasi anda.',
  },
];

export default function ResponsibleGamingPage() {
  const { lang, t } = useLanguage();
  const title = t(
    'Responsible Gaming Malaysia | Play Responsibly at MrCuci',
    'Permainan Bertanggungjawab Malaysia | Main Secara Bertanggungjawab di MrCuci'
  );
  const description = t(
    'MrCuci Responsible Gaming Malaysia guide: set gambling budgets and time limits, recognise warning signs, protect minors, assess your habits and seek support.',
    'Panduan Permainan Bertanggungjawab MrCuci Malaysia: tetapkan bajet dan had masa, kenali tanda amaran, lindungi bawah umur, nilai tabiat dan dapatkan sokongan.'
  );

  usePageSeo({
    title,
    description,
    path: '/responsible-gaming/',
    language: lang === 'bm' ? 'ms-MY' : 'en-MY',
    keywords: [
      'responsible gaming Malaysia',
      'MrCuci responsible gaming',
      'responsible gambling Malaysia',
      'safe online casino Malaysia',
      'gambling budget Malaysia',
      'casino time limits',
      'problem gambling warning signs',
      'stop chasing losses',
      'protect minors from gambling',
      'permainan bertanggungjawab Malaysia',
      'bantuan perjudian bermasalah Malaysia',
    ],
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          '@id': 'https://www.mrcuci.me/responsible-gaming/#breadcrumb',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: t('Home', 'Utama'), item: 'https://www.mrcuci.me/' },
            { '@type': 'ListItem', position: 2, name: t('Responsible Gaming', 'Permainan Bertanggungjawab'), item: 'https://www.mrcuci.me/responsible-gaming/' },
          ],
        },
        {
          '@type': 'Article',
          '@id': 'https://www.mrcuci.me/responsible-gaming/#article',
          headline: title,
          description,
          url: 'https://www.mrcuci.me/responsible-gaming/',
          inLanguage: lang === 'bm' ? 'ms-MY' : 'en-MY',
          dateModified: '2026-08-13',
          author: { '@type': 'Organization', '@id': 'https://www.mrcuci.me/#organization', name: 'MrCuci' },
          publisher: { '@type': 'Organization', '@id': 'https://www.mrcuci.me/#organization', name: 'MrCuci' },
          audience: {
            '@type': 'PeopleAudience',
            suggestedMinAge: 18,
            geographicArea: { '@type': 'Country', name: 'Malaysia' },
          },
          mainEntityOfPage: { '@id': 'https://www.mrcuci.me/responsible-gaming/#webpage' },
        },
        {
          '@type': 'FAQPage',
          '@id': 'https://www.mrcuci.me/responsible-gaming/#webpage',
          url: 'https://www.mrcuci.me/responsible-gaming/',
          name: title,
          description,
          breadcrumb: { '@id': 'https://www.mrcuci.me/responsible-gaming/#breadcrumb' },
          mainEntity: responsibleFaqs.map((faq) => ({
            '@type': 'Question',
            name: t(faq.en_q, faq.bm_q),
            acceptedAnswer: { '@type': 'Answer', text: t(faq.en_a, faq.bm_a) },
          })),
        },
      ],
    },
  });

  return (
    <div className="min-h-screen bg-[#080000] font-sans">
      <Navbar />

      <main className="pt-20">
        <section
          className="py-14 md:py-20 border-b border-[#d4a017]/10"
          style={{ background: 'linear-gradient(135deg, #0d0000 0%, #1a0303 55%, #0d0000 100%)' }}
        >
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <p className="text-[#d4a017] text-xs font-semibold tracking-widest uppercase mb-4">
              {t('18+ Player Safety Guide', 'Panduan Keselamatan Pemain 18+')}
            </p>
            <h1 className="text-3xl md:text-5xl font-black text-white leading-tight mb-5">
              <LinkedText>{t(
                'Responsible Gaming Malaysia – Play Responsibly at MrCuci',
                'Permainan Bertanggungjawab Malaysia – Main Secara Bertanggungjawab di MrCuci'
              )}</LinkedText>
            </h1>
            <div className="space-y-4 text-gray-300 text-sm md:text-base leading-[1.85] max-w-3xl">
              <p>
                <LinkedText>{t(
                  'At MrCuci Malaysia, responsible gaming means keeping online casino entertainment enjoyable, controlled and within your personal limits. Gambling should be treated as paid entertainment — never as a guaranteed way to earn money, solve financial problems or recover previous losses.',
                  'Di MrCuci Malaysia, permainan bertanggungjawab bermaksud memastikan hiburan kasino dalam talian kekal menyeronokkan, terkawal dan dalam had peribadi. Perjudian perlu dianggap sebagai hiburan berbayar — bukan cara terjamin untuk menjana wang, menyelesaikan masalah kewangan atau mendapatkan semula kerugian.'
                )}</LinkedText>
              </p>
              <p>
                <LinkedText>{t(
                  'Every casino game involves risk and there is always a possibility of losing the money wagered. This guide explains how to manage time and spending, recognise possible signs of problem gambling, protect minors and know when to stop or seek support.',
                  'Setiap permainan kasino melibatkan risiko dan wang yang dipertaruhkan boleh hilang. Panduan ini menerangkan cara mengurus masa dan perbelanjaan, mengenali tanda perjudian bermasalah, melindungi bawah umur dan mengetahui masa untuk berhenti atau mendapatkan sokongan.'
                )}</LinkedText>
              </p>
            </div>
          </div>
        </section>

        <section className="border-b border-[#d4a017]/10" style={{ background: '#0d0101' }}>
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-2xl font-black text-[#d4a017]">{guideSections.length}</p>
              <p className="text-gray-500 text-xs">{t('Guide topics', 'Topik panduan')}</p>
            </div>
            <div>
              <p className="text-2xl font-black text-[#d4a017]">{responsibleFaqs.length}</p>
              <p className="text-gray-500 text-xs">{t('FAQ answers', 'Jawapan FAQ')}</p>
            </div>
            <div>
              <p className="text-2xl font-black text-[#d4a017]">18+</p>
              <p className="text-gray-500 text-xs">{t('Adults only', 'Dewasa sahaja')}</p>
            </div>
          </div>
        </section>

        <nav
          aria-label={t('Responsible gaming contents', 'Kandungan permainan bertanggungjawab')}
          className="border-b border-[#d4a017]/10"
          style={{ background: '#0b0000' }}
        >
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-7">
            <h2 className="text-white font-black text-lg mb-4">
              {t('Responsible gaming guide contents', 'Kandungan panduan permainan bertanggungjawab')}
            </h2>
            <div className="grid sm:grid-cols-2 gap-2">
              {guideSections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="flex items-center gap-2 rounded-lg border border-[#d4a017]/10 px-4 py-3 text-xs font-semibold text-gray-400 hover:border-[#d4a017]/40 hover:text-[#d4a017] transition-colors"
                >
                  <i className="ri-arrow-right-s-line" />
                  {t(section.en_heading, section.bm_heading)}
                </a>
              ))}
              <a
                href="#responsible-gaming-faq"
                className="flex items-center gap-2 rounded-lg border border-[#d4a017]/10 px-4 py-3 text-xs font-semibold text-gray-400 hover:border-[#d4a017]/40 hover:text-[#d4a017] transition-colors"
              >
                <i className="ri-arrow-right-s-line" />
                {t('Responsible Gaming FAQ', 'FAQ Permainan Bertanggungjawab')}
              </a>
            </div>
          </div>
        </nav>

        <div className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16 space-y-14">
          {guideSections.map((section, sectionIndex) => (
            <section id={section.id} key={section.id} className="scroll-mt-24">
              <div className="mb-7">
                <p className="text-[#d4a017] text-xs font-black tracking-widest uppercase mb-2">
                  {t('Safety Topic', 'Topik Keselamatan')} {String(sectionIndex + 1).padStart(2, '0')}
                </p>
                <h2 className="text-2xl md:text-3xl font-black text-white mb-4">
                  {t(section.en_heading, section.bm_heading)}
                </h2>
                <div className="space-y-3 text-gray-400 text-sm leading-[1.9]">
                  {(lang === 'bm' ? section.bm_intro : section.en_intro).map((paragraph) => (
                    <p key={paragraph}><LinkedText>{paragraph}</LinkedText></p>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                {section.blocks.map((block) => {
                  const paragraphs = lang === 'bm' ? block.bm_paragraphs : block.en_paragraphs;
                  const points = lang === 'bm' ? block.bm_points : block.en_points;
                  return (
                    <article
                      key={block.en_heading}
                      className="rounded-2xl border border-[#d4a017]/10 p-5 md:p-7"
                      style={{ background: 'linear-gradient(135deg, #180303, #1e0404)' }}
                    >
                      <h3 className="text-base md:text-lg font-black text-white mb-4">
                        {t(block.en_heading, block.bm_heading)}
                      </h3>
                      <div className="space-y-3 text-gray-400 text-sm leading-[1.85]">
                        {paragraphs.map((paragraph) => (
                          <p key={paragraph}><LinkedText>{paragraph}</LinkedText></p>
                        ))}
                      </div>
                      {points && (
                        <div className="mt-5 pt-4 border-t border-white/5">
                          <h4 className="text-sm font-bold text-[#d4a017] mb-3">
                            {t('Practical points', 'Perkara praktikal')}
                          </h4>
                          <ul className="grid sm:grid-cols-2 gap-2 text-gray-400 text-sm">
                            {points.map((point) => (
                              <li key={point} className="flex items-start gap-2">
                                <i className="ri-check-line text-[#d4a017] mt-0.5" />
                                <span><LinkedText>{point}</LinkedText></span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            </section>
          ))}

          <section id="responsible-gaming-faq" className="scroll-mt-24">
            <p className="text-[#d4a017] text-xs font-black tracking-widest uppercase mb-2">FAQ</p>
            <h2 className="text-2xl md:text-3xl font-black text-white mb-6">
              {t('Responsible Gaming FAQ', 'FAQ Permainan Bertanggungjawab')}
            </h2>
            <div className="space-y-4">
              {responsibleFaqs.map((faq, index) => (
                <article
                  key={faq.en_q}
                  className="rounded-2xl border border-[#d4a017]/10 p-5 md:p-7"
                  style={{ background: 'linear-gradient(135deg, #180303, #1e0404)' }}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <span
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-xs font-black text-black shrink-0"
                      style={{ background: 'linear-gradient(135deg, #cc1100, #d4a017)' }}
                    >
                      {index + 1}
                    </span>
                    <h3 className="text-base md:text-lg font-black text-white">
                      {t(faq.en_q, faq.bm_q)}
                    </h3>
                  </div>
                  <p className="text-gray-400 text-sm leading-[1.85]"><LinkedText>{t(faq.en_a, faq.bm_a)}</LinkedText></p>
                </article>
              ))}
            </div>
          </section>

          <section className="rounded-2xl border border-[#d4a017]/20 p-6 md:p-8" style={{ background: 'linear-gradient(135deg, #200404, #2a0505)' }}>
            <h2 className="text-2xl font-black text-white mb-4">
              {t('Play for entertainment, stay in control', 'Main untuk hiburan, kekal terkawal')}
            </h2>
            <div className="space-y-4 text-gray-400 text-sm leading-[1.9]">
              <p>
                {t(
                  'Responsible gaming is not about finding a better way to win. It is about understanding risk and keeping gambling within healthy personal and financial boundaries.',
                  'Permainan bertanggungjawab bukan tentang mencari cara lebih baik untuk menang. Ia tentang memahami risiko dan memastikan perjudian dalam batas peribadi serta kewangan yang sihat.'
                )}
              </p>
              <p>
                {t(
                  'If gambling affects finances, relationships, work or emotional wellbeing, stop playing and seek appropriate support. Stay informed, set limits and remember that every real-money casino game can result in a loss.',
                  'Jika perjudian menjejaskan kewangan, hubungan, kerja atau kesejahteraan emosi, berhenti bermain dan dapatkan sokongan sesuai. Kekal bermaklumat, tetapkan had dan ingat bahawa setiap permainan wang sebenar boleh menyebabkan kerugian.'
                )}
              </p>
            </div>
            <div className="flex flex-wrap gap-3 mt-7">
              <a
                href="https://mrcuci.org/chatroom"
                className="inline-flex items-center gap-2 rounded-lg px-5 py-3 text-sm font-bold text-black"
                style={{ background: 'linear-gradient(135deg, #cc1100, #d4a017)' }}
              >
                <i className="ri-chat-3-line" />
                {t('Talk to Support', 'Hubungi Sokongan')}
              </a>
              <Link to="/terms/" className="inline-flex items-center gap-2 rounded-lg border border-[#d4a017]/40 px-5 py-3 text-sm font-bold text-[#d4a017]">
                {t('Terms & Conditions', 'Terma & Syarat')}
              </Link>
              <Link to="/faq/" className="inline-flex items-center gap-2 rounded-lg border border-[#d4a017]/40 px-5 py-3 text-sm font-bold text-[#d4a017]">
                {t('MrCuci FAQ', 'FAQ MrCuci')}
              </Link>
            </div>
            <p className="mt-6 text-xs font-bold text-gray-500">
              {t(
                '18+ only. Gambling involves financial risk.',
                '18+ sahaja. Perjudian melibatkan risiko kewangan.'
              )}
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
